/** Compact count for stats: raw below 1000, then one decimal + k (e.g. 2.1k). */
export function formatCount(num) {
  const n = Math.round(Number(num) || 0);
  if (n < 1000) return String(n);
  return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
}

/**
 * Human-readable number formatting (K, M, B). Reused across audience, patterns, analytics.
 */
export function formatNumber(num) {
  if (num == null) return "—";
  const n = Math.round(Number(num));
  if (n >= 1000000000) return `${(n / 1000000000).toFixed(1)}B`;
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

/**
 * Human-readable outlier/ratio: +50%, -20%, x2.5. Returns { text, above } or null.
 */
export function formatOutlier(ratio) {
  if (ratio == null || ratio === 0) return null;
  const isAbove = ratio >= 1;
  let text;
  if (isAbove && ratio > 101) {
    const label = Number.isInteger(ratio) ? String(ratio) : ratio.toFixed(1).replace(/\.0$/, "");
    text = `x${label}`;
  } else if (isAbove) {
    const pct = ((ratio - 1) * 100).toFixed(0);
    text = `+${pct}%`;
  } else {
    const pct = ((1 - ratio) * 100).toFixed(0);
    text = `-${pct}%`;
  }
  return { text, above: isAbove };
}

const MAX_BARS = 10;

/**
 * Build histogram bars from an array of numeric values (e.g. creator_views_all).
 * At most maxBars bars; the bar containing currentValue is marked highlighted.
 * Returns [{ count, isHighlighted }, ...] with length <= maxBars.
 */
export function buildHistogramBars(values, currentValue, maxBars = MAX_BARS) {
  const filtered = (values || []).map(Number).filter((n) => !Number.isNaN(n) && n != null);
  if (filtered.length === 0) return [];

  const sorted = [...filtered].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const span = max - min || 1;
  const n = Math.min(maxBars, sorted.length);

  const bars = Array(n).fill(0).map(() => ({ count: 0, isHighlighted: false }));

  for (const v of sorted) {
    let i = Math.min(n - 1, Math.floor(((v - min) / span) * n));
    if (i < 0) i = 0;
    bars[i].count += 1;
  }

  if (currentValue != null && !Number.isNaN(Number(currentValue))) {
    const cv = Number(currentValue);
    let hi = Math.min(n - 1, Math.floor(((cv - min) / span) * n));
    if (hi < 0) hi = 0;
    bars[hi].isHighlighted = true;
  }

  return bars;
}
